import { Localization, SettingsManager } from "@viamap/viamap2-common";
import * as M from 'maplibre-gl';
import { createContext } from "react";
import { LayerHierarchy, LayerHierarchyEndNode, LayerInfo, LayerVariabilityType, MapInfo, PictureLayoutType, PictureSize, PopupLayout } from "src/common/managers/Types";

import { MapFacadeAbstract, MitLatLng, MitMap } from "src/managers/MapFacade";
import { MapFacadeMaplibre, MitLayerHandleMaplibre } from "src/managers/MapFacadeMaplibre";
import { MapitLayerFactory } from "src/components/MapitLayerFactory";
import { OldHierarchy } from "src/managers/OldHierarchy";
import { Utils } from "@viamap/viamap2-common";
import { ReferenceGeomComposite } from "src/managers/ReferenceGeomComposite";
import { DemographyReportMetaData } from "src/components/DemographyReport";
import { MapitUtils } from "src/managers/MapitUtils";
import { FeatureType } from "./ApplicationState";
import { FeatureLayer, FeatureLayerList } from "src/managers/WmsLayerFunc";
import { MapState } from "src/compentsModus/EmbeddedVectorMapEnhanced";
import { DynamicDataDrivenLayer, parseFeatureLayer } from "src/managers/DynamicDataDrivenLayer";
import { VectorLayer } from "src/managers/VectorLayerFunc";
import { MitStorage } from "src/managers/MitStorage";
import { GeojsonWFS } from "src/managers/WFSGeojsonLayerFunc";
import { RememberImportsManager } from "src/managers/RememberImportsManager";
import { StatTracker } from "src/common/managers/StatTracker";

export enum MessageType {
   Error = "Error",
   Success = "Success"
}

// --------------------------------- Map Types --------------------------------------------
export type MitEvent = any;
// export type MitMap = any;
export enum MapType { Leaflet, Mapbox, Maplibre, Undefined }
export type MitBackgroundLayerHandle = string;
export type MitFeatureLayerHandle = string;
export type MitLayerId = number;
export type MitPopup = {
   popup: M.Popup,
   container: HTMLDivElement,
   latlng: MitLatLng,
   content: React.ReactNode,
   key: string
}
// export type MitLayerHandleMaplibre = MitLayerHandle;
export type MitId = string;


export type PopUpFeatureAvailability = {
   [featureNumber: string]: {
      available: boolean,
      stateActions?: { state: string, action: MapitStateActions | { type: any, payload: { name: string, pos: [number, number], tags?: string[], key?: string } } },
      callback?: any  // reverse compatibility. ToDo: must be removed.
   }
};

export type DataDrivenBackgroundLayer = {
   title: string,
   onMove: (map: MapFacadeAbstract, newLocation: MapState) => any
}

export enum MapInteractionState { Normal, Drawing, ClickToSetMarker, ClickToShowPropertyInfo, ClickToShowTravelTimePolygons, ClickToShowObliquePhotos, ClickToShowStreetView, QueryFeatures, ClickToShowPointDistanceTable, Override, EnforceFeatureLayerPopup, HentVurderingsEjendom, HentMatrikel, HentSamletFastEjendom, ZoomedIn, NSZoomedIn, HistoriskeKort };


// ---------------------------------------------------- STATE --------------------------------------------------------------
/*
Baggrundskortlag:
Lag som Viamap, Ortophoto, Dark etc.

Temalag:
Lag som Matrikel, Ejerlav, lokalplaner

Datalag:
Lag importeret fra Excel, Gjson osv.
*/

export interface MapitState {
   // ------- housekeepeing
   activeTransactions: { [guid: string]: any },
   message: string,
   messageType: MessageType,

   mapInfo: MapInfo,
   mapInteractionState: MapInteractionState,
   mapInteractionStateDefault: MapInteractionState,

   // colorRanges: any[],
   mapObjectRef?: MitMap,
   map: MapFacadeMaplibre,
   drawContent?: any,
   addedAttributions: number,

   backgroundLayers: { [layerId: MitId]: string },
   showBackgroundLayer: { [layerId: MitId]: boolean },

   featureLayers: { [layerId: MitId]: FeatureLayer },
   featureLayerHistory: { key: string, layerkeys: string[] }[]
   selectedFeatureLayerKeys: string[],

   layers: LayersType,
   printLayers: (
      { type: "Vector", handle: VectorLayer } |
      { type: "MitLayer", handle: MitLayerHandleMaplibre } |
      { type: "Raster", handle: FeatureLayer }
   )[]
   layerHierarchy: LayerHierarchy,

   // ------- window states by id
   mapPopout: MitPopup[];

   // ------- special states
   poiTypesToShow: string[];
   poiLayerHandle: MitLayerHandleMaplibre;
   viewPoi: boolean;
   // catchmentState: CatchmentState;

   // ------- data driven layers
   EsDrivenLayers: DataDrivenBackgroundLayer[],

   showUserLocation: boolean;
   demographyReport?: { metadata: DemographyReportMetaData, result: any }

   // ---------------------------- utility functions (read only) --------------------------
   getNextLayerId: (mapitState: MapitState) => MitLayerId;
}

export type ShowWindowType = {
   [windowId: string]: {
      stackingOrder: number,
      parameters?: any
   }
};

export type LayersType = { [layerId: MitId]: LayerInfo };

// --------------------------------- Utils --------------------------------------------

export function getNewId(): MitId {
   return MapitUtils.getNewUuid();
}

// ---------------------------------------------------- INITIAL STATE --------------------------------------------------------------

export function initialMapitState(): MapitState {
   const DefaultMapInfo: MapInfo = {
      popUpPictureLayout: PictureLayoutType.Center,
      popUpPictureSize: PictureSize.Medium,
      popUpLayout: PopupLayout.ShowAll,
      customerIconURL: "", // SettingsManager.getSystemSetting("customerIconURL", "", true),
      customerIconLink: "", // SettingsManager.getSystemSetting("customerIconLink", "", true)
      mapStyle: MitStorage.getValue("Mit-mapStyle") || {
         name: "Default",
         style: "Default",
         source: "Default",
         backgroundColor: "#ffffff",
         mods: [],
         defaultStyle: { style: "Default", name: "Default" },
         defaultBackgroundColor: "#ffffff",
      }
   };

   return {
      activeTransactions: {},
      message: "",
      messageType: MessageType.Success,
      mapPopout: [],

      showBackgroundLayer: {},
      layers: {},
      printLayers: [],
      backgroundLayers: {},
      featureLayers: {},
      featureLayerHistory: MitStorage.getValue('Mit-featureLayerHistory') || [],
      selectedFeatureLayerKeys: [],
      layerHierarchy: { id: 0, name: "Top Level", isCollapsed: false, children: [] },
      addedAttributions: 0,

      // colorRanges: [],
      // sessionInfo: 1 as unknown as SessionInfo,
      mapInfo: DefaultMapInfo,
      mapObjectRef: undefined,
      map: 1 as unknown as MapFacadeMaplibre,
      mapInteractionState: MapInteractionState.Normal,
      mapInteractionStateDefault: MapInteractionState.Normal,

      showUserLocation: false,

      poiTypesToShow: [],
      poiLayerHandle: new MitLayerHandleMaplibre(),
      viewPoi: true,
      // catchmentState: defaultCatchmentState,

      EsDrivenLayers: [],

      // ---------------------------- utility functions (read only) --------------------------
      getNextLayerId() {
         // Assign a unique layerid
         // find max existing layerId. First layerId will be 1.
         let maxVal = 0;
         Object.keys(this.layers).forEach((key) => {
            let val = this.layers[key].layerId;
            console.assert(!isNaN(val), "Array element is not a number");
            if (val > maxVal) { maxVal = val; }
         });
         return maxVal + 1;

      }
   }
}

// ---------------------------------------------------- ACTIONS --------------------------------------------------------------

export enum MapitStateActionType {
   TxStarted,
   TxCompleted,
   TxFailed,

   Restore,
   InitializeStandardLayers,
   AddExternalFeatureLayers,

   MapHasMoved, // To allow dynamic fetching of data for the viewport. E.g. POI's

   SetMessage,
   SetMapObjectRef, // Store the map object, to allow setting data directly.
   SetShowUserLocation,
   SetShowWindow,
   SetMapInteractionState,
   SetMapInteractionStateDefault,
   SetPoiTypesToShow,
   SetViewPoi,
   UpdateMapInfo,

   // -------- styling FeatureLayer -----
   StyleFeature,
   // --------- what to show -------------------
   SelectBackgroundLayer,
   SetFeatureLayerVisibility,
   RemoveFeatureLayerHistoryEntry,
   SetSingleFeatureLayerVisibility,
   SetDataLayerVisibility,
   // ---------- update layer list -------------
   AddDataLayer,
   UpdateDataLayer,
   RemoveDataLayer,
   LockUnlockDataLayer,
   ReserveLayerId,
   SetPrintLayers,
   // ---------- update layer hierarchy --------
   AddLayerToHierarchyAtTopLevel,
   UpdateLayerHierarchy,
   RemoveLayerInHierarchy,
   SetLayerHierarchy,
   UpdateNameInLayerHierarchy,
   // ----------- Popup functions --------------
   AddPopup, ClosePopups,
   // ----------- demography report ------------
   GenerateDemographyReportSuccess,

   // ----------- catchment areas --------------
   // SetCatchmentState,
   // GenerateCatchment

   // ----------- draw Context -----------------
   MapitSetDrawContext,
   ChangeMapStyle
}

export interface TxStarted {
   type: MapitStateActionType.TxStarted;
   payload: { guid: string, action: MapitStateActions };
}

export interface TxCompleted {
   type: MapitStateActionType.TxCompleted;
   payload: { guid: string, action: MapitStateActions };
}

export interface TxFailed {
   type: MapitStateActionType.TxFailed;
   payload: { guid: string, action: MapitStateActions, error: any };
}

export interface InitializeStandardLayers {
   type: MapitStateActionType.InitializeStandardLayers;
   payload: { hasAccessToFeatureFunction: (feat: FeatureType) => boolean };
}

export interface Restore {
   type: MapitStateActionType.Restore;
}

export interface AddExternalFeatureLayers {
   type: MapitStateActionType.AddExternalFeatureLayers;
   payload: { layers: FeatureLayerList }
}
export interface SetMessage {
   type: MapitStateActionType.SetMessage;
   payload: { item: string };
}

export interface SetMapInteractionState {
   type: MapitStateActionType.SetMapInteractionState;
   payload: { value: MapInteractionState };
}

export interface SetMapInteractionStateDefault {
   type: MapitStateActionType.SetMapInteractionStateDefault;
}

export interface SelectBackgroundLayer {
   type: MapitStateActionType.SelectBackgroundLayer;
   payload: { key: MitBackgroundLayerHandle };
}

export interface SetFeatureLayerVisibility {
   type: MapitStateActionType.SetFeatureLayerVisibility;
   payload: { key: string, visible: boolean, layers: { key: MitFeatureLayerHandle, layer?: FeatureLayer }[], history?: boolean }
}
export interface SetSingleFeatureLayerVisibility {
   type: MapitStateActionType.SetSingleFeatureLayerVisibility;
   payload: { key: MitFeatureLayerHandle, visible: boolean, layer: FeatureLayer | undefined };
}

export interface AddDataLayer {
   type: MapitStateActionType.AddDataLayer;
   payload: { layerInfo: LayerInfo, zoomToFocus?: boolean, showAsDefault?: boolean, featureAvailability?: PopUpFeatureAvailability };
   callbacks?: {
      onLayerAdded?: (layerInfo: LayerInfo) => void
   }
}

export interface RemoveDataLayer {
   type: MapitStateActionType.RemoveDataLayer;
   payload: { id: MitLayerId, keepInHierarchy?: boolean };
}


export interface ReserveLayerId {
   type: MapitStateActionType.ReserveLayerId;
   payload: { id: MitLayerId };
}
export interface UpdateDataLayer {
   type: MapitStateActionType.UpdateDataLayer;
   payload: { id: MitLayerId, layerInfo: LayerInfo };
}

export interface SetDataLayerVisibility {
   type: MapitStateActionType.SetDataLayerVisibility;
   payload: { id: MitLayerId, visible: boolean };
}

export interface SetMapObjectRef {
   type: MapitStateActionType.SetMapObjectRef;
   payload: { item: MapFacadeMaplibre };
}
export interface RemoveFeatureLayerHistoryEntry {
   type: MapitStateActionType.RemoveFeatureLayerHistoryEntry;
   payload: { key: string };
}

export interface StyleFeature {
   type: MapitStateActionType.StyleFeature;
   payload: { key: string, value: any };
}

export interface SetShowUserLocation {
   type: MapitStateActionType.SetShowUserLocation;
   payload: { show: boolean };
}

export interface SetPoiTypesToShow {
   type: MapitStateActionType.SetPoiTypesToShow;
   payload: { types: string[] };
}

export interface SetViewPoi {
   type: MapitStateActionType.SetViewPoi;
   payload: { active: boolean };
}

export interface UpdateMapInfo {
   type: MapitStateActionType.UpdateMapInfo;
   payload: { mapInfo: Partial<MapInfo> };
}

// export interface SetLayers {
//    type: MapitStateActionType.SetLayers;
//    payload: { item: LayersType };
// }

export interface SetPrintLayers {
   type: MapitStateActionType.SetPrintLayers;
   payload: { printLayers: MapitState["printLayers"] };
}

export interface LockUnlockDataLayer {
   type: MapitStateActionType.LockUnlockDataLayer;
   payload: { id: MitLayerId, unlock?: boolean };
}

export interface SetLayerHierarchy {
   type: MapitStateActionType.SetLayerHierarchy;
   payload: { hierarchy: LayerHierarchy };
}
export interface AddLayerToHierarchyAtTopLevel {
   type: MapitStateActionType.AddLayerToHierarchyAtTopLevel;
   payload: { id: number };
}
export interface RemoveLayerInHierarchy {
   type: MapitStateActionType.RemoveLayerInHierarchy;
   payload: { id: number };
}
export interface UpdateLayerHierarchy {
   type: MapitStateActionType.UpdateLayerHierarchy;
   payload: { id: number };
}
export interface UpdateNameInLayerHierarchy {
   type: MapitStateActionType.UpdateNameInLayerHierarchy;
   payload: { hierarchy: LayerHierarchy }
}
export interface AddPopup {
   type: MapitStateActionType.AddPopup;
   payload: { latlng: MitLatLng; content: React.ReactNode, closeOthers: boolean }
}

export interface ChangeMapStyle {
   type: MapitStateActionType.ChangeMapStyle;
   payload: { style: Partial<MapitState["mapInfo"]["mapStyle"]>, userAction: boolean };
}

export interface ClosePopups {
   type: MapitStateActionType.ClosePopups;
   payload: { closeAll: boolean }
}

export interface GenerateDemographyReportSuccess {
   type: MapitStateActionType.GenerateDemographyReportSuccess;
   payload: { metadata: DemographyReportMetaData, result: any };
}

export interface MapitSetDrawContext {
   type: MapitStateActionType.MapitSetDrawContext;
   payload: { drawContent: any }
}

// export interface SetCatchmentState {
//    type: MapitStateActionType.SetCatchmentState;
//    payload: { state: CatchmentState };
// }

// -------------------- utility functions to create an Action object ---------------------------------

export const actionTxStarted = (guid: string, action: MapitStateActions): TxStarted => ({
   type: MapitStateActionType.TxStarted,
   payload: { guid, action }
});

export const actionTxCompleted = (guid: string, action: MapitStateActions): TxCompleted => ({
   type: MapitStateActionType.TxCompleted,
   payload: { guid, action }
});

export const actionTxFailed = (guid: string, action: MapitStateActions, error: any): TxFailed => ({
   type: MapitStateActionType.TxFailed,
   payload: { guid, action, error }
});

export const actionSetMessage = (item: string): SetMessage => ({
   type: MapitStateActionType.SetMessage,
   payload: { item }
});

export const actionSetDrawContext = (drawContent: any): MapitSetDrawContext => ({
   type: MapitStateActionType.MapitSetDrawContext,
   payload: { drawContent }
})

// export const actionSetSessionInfo = (item: SessionInfo): SetSessionInfo => ({
//    type: MapitStateActionType.SetSessionInfo,
//    payload: { item }
// });

export const SetPrintLayers = (printLayers: MapitState["printLayers"]): SetPrintLayers => ({
   type: MapitStateActionType.SetPrintLayers,
   payload: { printLayers }
})

export const LockUnlockDataLayer = (id: MitLayerId, unlock?: boolean): LockUnlockDataLayer => ({
   type: MapitStateActionType.LockUnlockDataLayer,
   payload: { id, unlock }

});

export const actionStyleFeature = (key: string, value: any): StyleFeature => ({
   type: MapitStateActionType.StyleFeature,
   payload: { key, value },
})

export const actionSetMapInteractionState = (value: MapInteractionState): SetMapInteractionState => ({
   type: MapitStateActionType.SetMapInteractionState,
   payload: { value }
});

export const actionSetMapInteractionStateDefault = (): SetMapInteractionStateDefault => ({
   type: MapitStateActionType.SetMapInteractionStateDefault
})

export const actionSetShowUserLocation = (show: boolean): SetShowUserLocation => ({
   type: MapitStateActionType.SetShowUserLocation,
   payload: { show }
});

export const actionSetPoiTypesToShow = (types: string[]): SetPoiTypesToShow => ({
   type: MapitStateActionType.SetPoiTypesToShow,
   payload: { types }
});

export const actionSetViewPoi = (active: boolean): SetViewPoi => ({
   type: MapitStateActionType.SetViewPoi,
   payload: { active }
});

export const actionUpdateMapInfo = (mapInfo: Partial<MapInfo>): UpdateMapInfo => ({
   type: MapitStateActionType.UpdateMapInfo,
   payload: { mapInfo: mapInfo }
});

export const actionAddDataLayer = (layerInfo: LayerInfo, zoomToFocus?: boolean, showAsDefault: boolean = true, featureAvailability?: PopUpFeatureAvailability): AddDataLayer => ({
   type: MapitStateActionType.AddDataLayer,
   payload: { layerInfo, zoomToFocus, showAsDefault, featureAvailability }
});

export const actionUpdateDataLayer = (id: MitLayerId, layerInfo: LayerInfo): UpdateDataLayer => ({
   type: MapitStateActionType.UpdateDataLayer,
   payload: { layerInfo, id }
});

export const actionRemoveDataLayer = (id: MitLayerId, keepInHierarchy?: boolean): RemoveDataLayer => ({
   type: MapitStateActionType.RemoveDataLayer,
   payload: { id, keepInHierarchy }
});

export const actionRemoveFeatureLayerHistoryEntry = (key: string): RemoveFeatureLayerHistoryEntry => ({
   type: MapitStateActionType.RemoveFeatureLayerHistoryEntry,
   payload: { key }
})

export const actionSetFeatureLayerVisibility = (key: MitFeatureLayerHandle, visible: boolean, layers: { key: string, layer?: FeatureLayer }[], history?: boolean): SetFeatureLayerVisibility => ({
   type: MapitStateActionType.SetFeatureLayerVisibility,
   payload: { key, visible, layers, history }
});

export const actionSetSingleFeatureLayerVisibility = (key: MitFeatureLayerHandle, visible: boolean, layer?: FeatureLayer): SetSingleFeatureLayerVisibility => ({
   type: MapitStateActionType.SetSingleFeatureLayerVisibility,
   payload: { key, visible, layer }
});

export const changeMapStyle = (style: Partial<MapitState["mapInfo"]["mapStyle"]>, userAction: boolean = false): ChangeMapStyle => ({
   type: MapitStateActionType.ChangeMapStyle,
   payload: { style, userAction }
})

export const actionSetDataLayerVisibility = (id: MitLayerId, visible: boolean): SetDataLayerVisibility => ({
   type: MapitStateActionType.SetDataLayerVisibility,
   payload: { id, visible }
});

export const actionSelectBackgroundLayer = (key: MitBackgroundLayerHandle): SelectBackgroundLayer => ({
   type: MapitStateActionType.SelectBackgroundLayer,
   payload: { key }
});

export const actionSetLayerHierarchy = (hierarchy: LayerHierarchy): SetLayerHierarchy => ({
   type: MapitStateActionType.SetLayerHierarchy,
   payload: { hierarchy }
});

export const actionUpdateNameInLayerHierarchy = (hierarchy: LayerHierarchy): UpdateNameInLayerHierarchy => ({
   type: MapitStateActionType.UpdateNameInLayerHierarchy,
   payload: { hierarchy }
});

export const actionAddPopup = (latlng: MitLatLng, content: React.ReactNode, closeOthers: boolean): AddPopup => ({
   type: MapitStateActionType.AddPopup,
   payload: { latlng, content, closeOthers }

})
export const actionClosePopups = (closeAll: boolean): ClosePopups => ({
   type: MapitStateActionType.ClosePopups,
   payload: { closeAll }
})

export const actionAddExternalFeatureLayers = (layers: FeatureLayerList): AddExternalFeatureLayers => ({
   type: MapitStateActionType.AddExternalFeatureLayers,
   payload: { layers }
})

// export const actionSetCatchmentState = (state: CatchmentState) : SetCatchmentState => ({
//    type: MapitStateActionType.SetCatchmentState,
//    payload: { state }

// })

export const actionGenerateDemographyReportSuccess = (metadata: DemographyReportMetaData, result: any): GenerateDemographyReportSuccess => ({
   type: MapitStateActionType.GenerateDemographyReportSuccess,
   payload: { metadata, result }

})

// ---------------------------------------------------- ACTIONS --------------------------------------------------------------

export type MapitStateActionsTransActional = SetMessage | AddDataLayer;
export type MapitStateActionsNonTransActional = TxStarted | TxCompleted | TxFailed |
   SetMessage | SelectBackgroundLayer |
   SetFeatureLayerVisibility |
   SetSingleFeatureLayerVisibility |
   RemoveFeatureLayerHistoryEntry |
   ReserveLayerId |
   AddDataLayer |
   UpdateDataLayer |
   RemoveDataLayer |
   SetMapObjectRef |
   StyleFeature |
   // SetSessionInfo |
   SetDataLayerVisibility |
   MapitSetDrawContext |
   SetMapInteractionState | SetMapInteractionStateDefault |
   // SetLayers | 
   SetPrintLayers |
   LockUnlockDataLayer |
   SetLayerHierarchy |
   SetShowUserLocation |
   AddLayerToHierarchyAtTopLevel |
   UpdateLayerHierarchy |
   RemoveLayerInHierarchy |
   UpdateNameInLayerHierarchy |
   AddPopup | ClosePopups | ChangeMapStyle |
   AddExternalFeatureLayers |
   InitializeStandardLayers | GenerateDemographyReportSuccess | SetPoiTypesToShow | SetViewPoi | UpdateMapInfo | Restore
   // | SetCatchmentState
   ;
export type MapitStateActions = MapitStateActionsTransActional | MapitStateActionsNonTransActional;


// ---------------------------------------------------- TRANSACTIONAL REDUCER --------------------------------------------------------------

export function transactionalMapitStateReducer(action: MapitStateActionsTransActional, dispatch: any) {

   switch (action.type) {
      // case MapitStateActionType.SetMessage: {
      //    }
      //    break;
      case MapitStateActionType.AddDataLayer: {

         //   // check to see that all needed ReferenceGeom has loaded.
         //   // otherwise async 
         if (MapitUtils.isAreaLayer(action.payload.layerInfo.type)) {
            if (!ReferenceGeomComposite.isRequiredGeomDataLoaded(action.payload.layerInfo.type)) {
               let txId = Date.now().toString();
               dispatch(actionTxStarted(txId, action));
               // ToDo: show a 'Loading...' message to the user.

               //  appMessageDispatch(actionSetInfoMessage(Localization.getText("Loading..."));
               // Create placeholder for pending layer creation - otherwise next layer will overwrite this position.
               dispatch({ type: MapitStateActionType.ReserveLayerId, payload: { id: action.payload.layerInfo.layerId, layerInfo: action.payload.layerInfo } });
               ReferenceGeomComposite.ensureRequiredGeomData(action.payload.layerInfo.type)
                  .then(() => {
                     // loopback then loading is done.
                     transactionalMapitStateReducer(action, dispatch);
                  })
                  .catch(error => {
                     dispatch(actionTxFailed(txId, action, "Loading failed " + error.message));
                     // appMessageDispatch(actionSetErrorMessage();
                  }).finally(() => {
                     dispatch(actionTxCompleted(txId, action));
                     // appMessageDispatch(actionClearInfoMessage(;
                  });
               return;
            }
         }
         dispatch(action);
         break;
      }

      default:
         // proceed directly to non-transational reducer for other actions
         dispatch(action);
   }
}

// ---------------------------------------------------- REDUCER --------------------------------------------------------------

export function MapitStateReducer(state: MapitState, action: MapitStateActions): MapitState {

   switch (action.type) {

      case MapitStateActionType.TxStarted:
         return {
            ...state,
            activeTransactions: { ...state.activeTransactions, [action.payload.guid]: action }
         };

      case MapitStateActionType.TxFailed: {
         let tmp = { ...state.activeTransactions };
         delete tmp[action.payload.guid];
         console.error(action.payload.error);
         let msg = Utils.formatString(Localization.getTextSafeMode("Error. Transaction {txname} returned with {error}"), {
            txname: Localization.getTextSafeMode(MapitStateActionType[action.payload.action.type]),
            error: Localization.getTextSafeMode(action.payload.error)
         });
         // Logger.logError("viamapLiceningState", MapitStateActionType[action.payload.action.type], msg);
         return {
            ...state,
            message: msg,
            messageType: MessageType.Error
         };
      }

      case MapitStateActionType.TxCompleted: {
         let tmp = { ...state.activeTransactions };
         delete tmp[action.payload.guid];
         return {
            ...state,
            activeTransactions: tmp,
            message: ""
         };
      }

      // ---------------------------------- initialization ---------------------------------------------------

      case MapitStateActionType.InitializeStandardLayers: {
         function getFeatureLayerList(): FeatureLayer[] {
            let customLayerSetting = SettingsManager.getSystemSetting("customBackgroundLayers");
            let standardLayerSetting = SettingsManager.getSystemSetting("standardBackgroundLayers");
            let dynamicLayerSetting = SettingsManager.getSystemSetting("dynamicBackgroundLayers");
            let vectorLayerSetting = SettingsManager.getSystemSetting("vectorBackgroundLayers");
            return [...customLayerSetting, ...standardLayerSetting, ...dynamicLayerSetting, ...vectorLayerSetting].map((a) => {
               return parseFeatureLayer(a)
            }).filter((a) => a) as FeatureLayer[]
         }

         let selectableLayers: FeatureLayerList = {};
         let featureLayers = getFeatureLayerList();
         featureLayers?.forEach((a, idx) => {
            a.layer.priority = a.layer.priority === 999 ? idx + 10 : a.layer.priority
         })
         featureLayers?.sort((a, b) => a.layer.priority - b.layer.priority).forEach((featureLayer: FeatureLayer) => {
            const ftl = featureLayer.layer
            const inActive = ("isActive" in ftl && ftl.isActive === false)
            if (action.payload.hasAccessToFeatureFunction(ftl.licenseFeature) && !inActive) {
               selectableLayers = {
                  ...selectableLayers,
                  [ftl.label]: featureLayer
               };
            }
         });

         return {
            ...state,
            featureLayers: selectableLayers
         };
      }

      case MapitStateActionType.Restore: {
         const orderedList = OldHierarchy.getAllIdOrderedTopBottom(state.layerHierarchy)
         orderedList.toReversed().forEach((key) => {
            const layerInfo = state.layers[key];
            if (!layerInfo) {
               return
            }
            if (layerInfo.visible) {
               layerInfo.handle.addTo(state.map)
               state.map.setLayerVisibility(layerInfo.handle, true)
            }
         })

         state.selectedFeatureLayerKeys.map((a) => {
            let layerSpec = state.featureLayers[a]

            if (layerSpec instanceof VectorLayer || layerSpec instanceof GeojsonWFS) {
               state.map.addVectorLayer(layerSpec);
               return
            }
            if (layerSpec instanceof DynamicDataDrivenLayer) {
               state.map.addDynamicLayer(layerSpec);
               return
            }
            if (layerSpec) {
               state.map.addFeatureLayer(a, layerSpec);
            }
         })
         StatTracker.open("FeatureLayer", ...state.selectedFeatureLayerKeys)
         return {
            ...state
         }
      }

      case MapitStateActionType.AddExternalFeatureLayers: {
         let selectableLayers: FeatureLayerList = {};
         let featureLayers = Object.keys(action.payload.layers).map((a) => parseFeatureLayer(action.payload.layers[a])).filter((a) => a) as FeatureLayer[];
         featureLayers?.forEach((a, idx) => {
            a.layer.priority = a.layer.priority === 999 ? idx + 10 : a.layer.priority
         })
         featureLayers?.sort((a, b) => a.layer.priority - b.layer.priority).forEach((featureLayer: FeatureLayer) => {
            const ftl = featureLayer.layer
            selectableLayers = {
               ...selectableLayers,
               [ftl.label]: featureLayer
            };
         });

         return {
            ...state,
            featureLayers: { ...state.featureLayers, ...selectableLayers },
         };
      }

      // --------------------------------- setters -----------------------------------------------------
      case MapitStateActionType.SetMessage: {
         return {
            ...state, message: action.payload.item
         };
      }


      case MapitStateActionType.SetMapInteractionState: {

         if ([MapInteractionState.Override, MapInteractionState.Drawing, MapInteractionState.Normal, MapInteractionState.ZoomedIn, MapInteractionState.NSZoomedIn].includes(action.payload.value)) {
            return {
               ...state,
               mapInteractionStateDefault: action.payload.value,
               mapInteractionState: action.payload.value
            };
         }

         return {
            ...state,
            mapInteractionState: action.payload.value
         };
      }

      case MapitStateActionType.SetMapInteractionStateDefault: {
         return {
            ...state,
            mapInteractionState: state.mapInteractionStateDefault
         };
      }

      case MapitStateActionType.SetMapObjectRef: {
         return {
            ...state,
            mapObjectRef: action.payload.item,
            map: action.payload.item
         };
      }

      case MapitStateActionType.SetPoiTypesToShow: {
         return {
            ...state,
            poiTypesToShow: action.payload.types
         };
      }

      case MapitStateActionType.SetViewPoi: {
         return {
            ...state,
            viewPoi: action.payload.active
         }
      }

      case MapitStateActionType.SetShowUserLocation: {
         state.map.showUserLocation(action.payload.show);
         return {
            ...state,
            showUserLocation: action.payload.show
         };
      }

      case MapitStateActionType.RemoveFeatureLayerHistoryEntry: {
         return {
            ...state,
            featureLayerHistory: state.featureLayerHistory.filter((a) => a.key !== action.payload.key)
         }
      }

      case MapitStateActionType.SetFeatureLayerVisibility: {
         let history: Partial<MapitState> = {}
         if (action.payload.history) {
            const maxCount = (SettingsManager.getSystemSetting("maxNumberOfRecentLayersToShow") || 5)
            history = {
               featureLayerHistory: [
                  { key: action.payload.key, layerkeys: action.payload.layers.map((a) => a.key) },
                  ...state.featureLayerHistory.filter((a) => a.key !== action.payload.key)
               ].filter((a, idx) => idx < maxCount)
            }
            MitStorage.setValue('Mit-featureLayerHistory', history.featureLayerHistory)
         }
         let stateL = state
         action.payload.layers.forEach((singeLayer) => {
            stateL = MapitStateReducer(stateL, {
               type: MapitStateActionType.SetSingleFeatureLayerVisibility,
               payload: { key: singeLayer.key, visible: action.payload.visible, layer: singeLayer.layer }
            })
         });
         return { ...stateL, ...history }
      }

      // TODO: Most logic should be moved to MapFacade, MapFacade should expose a single method to add all type of featureLayers
      case MapitStateActionType.SetSingleFeatureLayerVisibility: {
         let isCustomLayer = action.payload.key.split("\\").length > 1;
         let layerSpec = parseFeatureLayer(action.payload.layer?.layer) || state.featureLayers[action.payload.key];
         if (isCustomLayer) {
            let cleanKey = action.payload.key.split("\\").at(-1)
            if (!cleanKey) {
               return state
            }
            layerSpec = state.featureLayers[cleanKey];
            layerSpec.layer.paint = action.payload.layer?.layer?.paint || layerSpec.layer.paint;
            layerSpec.layer.layerOptions = action.payload.layer?.layer?.layerOptions || layerSpec.layer.layerOptions;
            layerSpec.layer.variantValues = action.payload.layer?.layer?.variantValues || layerSpec.layer.variantValues;
         }

         if (action.payload.visible) {
            StatTracker.open("FeatureLayer", layerSpec?.layer?.label || "Unknown")
         } else {
            StatTracker.close("FeatureLayer", layerSpec?.layer?.label || "Unknown")
         }


         if (layerSpec instanceof VectorLayer || layerSpec instanceof GeojsonWFS) {
            if (action.payload.visible) {
               state.map.addVectorLayer(layerSpec)
               let override = {};
               if (!Object.keys(state.featureLayers).includes(action.payload.key)) {
                  layerSpec.layer.group = "hidden"
                  override = { featureLayers: { ...state.featureLayers, [action.payload.key]: layerSpec } }
               }
               return {
                  ...state,
                  selectedFeatureLayerKeys: [...state.selectedFeatureLayerKeys.filter((val) => { return val !== action.payload.key; }), action.payload.key],
                  ...override
               }
            } else {
               state.map.removeVectorLayer(layerSpec)
               return {
                  ...state,
                  selectedFeatureLayerKeys: [...state.selectedFeatureLayerKeys.filter((val) => { return val !== action.payload.key; })],
               }
            }
         }
         if (!layerSpec) {
            return { ...state }
         }
         let override = {};
         if (action.payload.layer?.layer) {
            if (!Object.keys(state.featureLayers).includes(action.payload.key) || state.featureLayers[action.payload.key].layer.group == "hidden") {
               layerSpec.layer.group = "hidden"
            }
            override = { featureLayers: { ...state.featureLayers, [action.payload.key]: layerSpec } }
         }
         let NewEsDrivenLayers = [...state.EsDrivenLayers];
         const isDataDriven = layerSpec instanceof DynamicDataDrivenLayer
         if (isDataDriven) {
            if (action.payload.visible) {
               state.map.addDynamicLayer(layerSpec as DynamicDataDrivenLayer);
               return {
                  ...state,
                  selectedFeatureLayerKeys: [...state.selectedFeatureLayerKeys.filter((val) => { return val !== action.payload.key; }), action.payload.key],
                  ...override
               }
            } else {
               state.map.removeDynamicLayer(layerSpec as DynamicDataDrivenLayer);
               return {
                  ...state,
                  selectedFeatureLayerKeys: state.selectedFeatureLayerKeys.filter((val) => { return val !== action.payload.key; }),
                  ...override
               }
            }

         }
         if (action.payload.visible) {
            if (!state.selectedFeatureLayerKeys.includes(action.payload.key)) {
               if (!isDataDriven) state.map.addFeatureLayer(action.payload.key, layerSpec);
            }
            return {
               ...state,
               EsDrivenLayers: [...NewEsDrivenLayers],
               selectedFeatureLayerKeys: [...state.selectedFeatureLayerKeys.filter((val) => { return val !== action.payload.key; }), action.payload.key],
               ...override
            }
         } else {
            if (!isDataDriven) state.map.removeFeatureLayer(action.payload.key);
            return {
               ...state,
               EsDrivenLayers: [...NewEsDrivenLayers],
               selectedFeatureLayerKeys: [...state.selectedFeatureLayerKeys.filter((val) => { return val !== action.payload.key; })],
               ...override
            }
         };
      }

      // ------------------------------------------------- background layers -----------------------------------------

      case MapitStateActionType.SelectBackgroundLayer: {
         let backgroundLayers: { [key: string]: string } = {
            'Map': 'Viamap', 'Orthophoto': 'Aerial',
            'OrthophotoNoWaterMask': 'Areal not masked', 'BlankMap': 'Blank Map'
         };
         action.payload.key = action.payload.key || 'Map';
         switch (action.payload.key as keyof typeof backgroundLayers) {
            case 'Map':
               return MapitStateReducer(state, {
                  type: MapitStateActionType.ChangeMapStyle,
                  payload: {
                     style: { mods: [], name: "Default" },
                     userAction: false
                  }
               });
               break;
            case 'Orthophoto':
            case 'OrthophotoNoWaterMask':
               return MapitStateReducer(state, {
                  type: MapitStateActionType.ChangeMapStyle,
                  payload: {
                     style: { mods: ["Ortophoto"], name: "Default" },
                     userAction: false
                  }
               });
            case 'BlankMap':
               return MapitStateReducer(state, {
                  type: MapitStateActionType.ChangeMapStyle,
                  payload: {
                     style: { mods: [], name: 'Blank' },
                     userAction: false
                  }
               });
         }
         return state
      }
      // ------------------------------------------------- feature layers -----------------------------------------------

      // ------------------------------------------------- data layers -----------------------------------------------
      case MapitStateActionType.AddLayerToHierarchyAtTopLevel: {
         let layerHierarchy = state.layerHierarchy;
         layerHierarchy.children.push({ id: action.payload.id });
         return {
            ...state,
            layerHierarchy: layerHierarchy
         };
      }

      case MapitStateActionType.UpdateMapInfo: {
         return {
            ...state,
            mapInfo: { ...state.mapInfo, ...action.payload.mapInfo }
         }
      }

      case MapitStateActionType.SetLayerHierarchy: {
         let ids = OldHierarchy.getAllIdOrderedTopBottom(action.payload.hierarchy).reverse()
         ids.forEach((a) => {
            if (state.layers[a].visible) {
               state.layers[a]?.handle.moveLayer(state.map)
            }
         });

         return {
            ...state,
            layerHierarchy: OldHierarchy.fixLayerHierarchy(action.payload.hierarchy)
         }
      }
      case MapitStateActionType.UpdateNameInLayerHierarchy: {
         const changes = action.payload.hierarchy;
         const parent = OldHierarchy.getParentGroup(changes.id, state.layerHierarchy)
         const id = parent?.children.findIndex((a) => a.id === changes.id)
         if (parent && (id !== undefined)) {
            parent.children[id] = { ...changes }
         }
         return {
            ...state,
            layerHierarchy: OldHierarchy.fixLayerHierarchy({ ...state.layerHierarchy })
         }
      }

      case MapitStateActionType.ReserveLayerId: {
         return {
            ...state,
            layers: { ...state.layers, [action.payload.id]: {} }
         }
      }

      case MapitStateActionType.SetDataLayerVisibility: {
         if (action.payload.visible) {
            const orderedList = OldHierarchy.getAllIdOrderedTopBottom(state.layerHierarchy)
            const beforeHandles = [...orderedList].splice(0, orderedList.findIndex((a) => a == action.payload.id) + 1 || 0).filter((a) => a !== action.payload.id)
            state.map.addLayer(state.layers[action.payload.id].handle, beforeHandles.map((a) => state.layers[a]?.visible && state.layers[a]?.handle).filter((a) => a));
         } else {
            state.map.removeLayer(state.layers[action.payload.id].handle)
         }
         return {
            ...state, layers: { ...state.layers, [action.payload.id]: { ...state.layers[action.payload.id], visible: action.payload.visible } }
         }
      }

      case MapitStateActionType.AddPopup: {
         const popup = new M.Popup()
         const newCon = document.createElement('div');
         newCon.style.textAlign = "left";
         newCon.className = "MapitStatePopup";
         popup.setLngLat(action.payload.latlng);
         popup.setDOMContent(newCon);
         popup.setOffset({ 'bottom': [0, -10] } as any)
         popup.addTo(state.map.getMapPop() as any);
         if (action.payload.closeOthers) {
            state.mapPopout.forEach((a) => {
               a.popup.remove()
            })
         }
         return { ...state, mapPopout: [...state.mapPopout.filter((a) => a.container.isConnected), { container: newCon, popup: popup, latlng: action.payload.latlng, content: action.payload.content, key: "id" + state.mapPopout.length }] }
      }

      case MapitStateActionType.ClosePopups: {
         if (action.payload.closeAll) {
            state.mapPopout.forEach((a) => {
               a.popup.remove()
            })
         }
         return { ...state, mapPopout: [...state.mapPopout.filter((a) => a.container.isConnected)] }
      }

      case MapitStateActionType.StyleFeature: {
         const key = action.payload.key
         const layer = state.featureLayers[key]
         const layerCopy = layer.copy()
         const value = action.payload.value
         layer.layer.variantValues = value

         layer.setStyling(value)
         const newUrl = layerCopy.getTileURL() !== layer.getTileURL()
         const isDataDriven = layer instanceof DynamicDataDrivenLayer
         if (layer instanceof VectorLayer || layer instanceof GeojsonWFS) {
            state.map.updateVectorLayer(layer, value)
            return { ...state, featureLayers: { ...state.featureLayers } }
         }
         if ((!isDataDriven) && state.selectedFeatureLayerKeys.includes(key)) {
            if (newUrl) {
               state.map.removeFeatureLayer(key)
               state.map.addFeatureLayer(key, layer)
            } else {
               state.map.updateFeatureLayer(key, layer)
            }
         }
         if ((isDataDriven) && state.selectedFeatureLayerKeys.includes(key)) {
            state.map.updateDynamicLayer(key, layer as DynamicDataDrivenLayer, value)
         }
         return { ...state, featureLayers: { ...state.featureLayers } }
      }

      case MapitStateActionType.SetPrintLayers: {
         return {
            ...state, printLayers: action.payload.printLayers
         }
      }

      case MapitStateActionType.AddDataLayer: {
         let _layerInfo: LayerInfo = { ...action.payload.layerInfo }
         let layerVisibility = (_layerInfo.visible) ?? true
         if (_layerInfo.layerId === -1) {
            _layerInfo.layerId = state.getNextLayerId(state);
         }

         _layerInfo.visible = _layerInfo.visible ?? action.payload.showAsDefault ?? true;
         let { layerHandle, layerInfo: newLayerInfo } = MapitLayerFactory.createADataLayer(state.map, _layerInfo, state.mapInfo, action.payload.featureAvailability || {});
         let useSavedImportSettings = SettingsManager.getSystemSetting("useSavedImportSettings", false);
         if (useSavedImportSettings && newLayerInfo.layerVariability === LayerVariabilityType.NormalLayer) {
            RememberImportsManager.rememberStyling({ ...newLayerInfo, visible: true });
         }
         if (layerHandle) {
            if (layerVisibility) {
               const orderedList = OldHierarchy.getAllIdOrderedTopBottom(state.layerHierarchy)
               const beforeHandles = [...orderedList].splice(0, orderedList.findIndex((a) => a == newLayerInfo.layerId) + 1 || 0).filter((a) => a !== newLayerInfo.layerId)
               layerHandle.addTo(state.map, beforeHandles.map((a) => (state.layers[a]?.visible == true) && state.layers[a]?.handle).filter((a) => a));
               state.map.setLayerVisibility(layerHandle as any, layerVisibility)
               if (action.payload.zoomToFocus) {
                  state.map.zoomToFeature(layerHandle as any);
               }
            }

            // Store new layer and handle in state
            const TestInHierarchy = (a: LayerHierarchy | LayerHierarchyEndNode, id: number, idx?: number) => {
               let _idx = idx ?? 0;
               if (_idx < 0) {
                  return true
               }
               if (a.id === newLayerInfo.layerId) {
                  return true
               }
               if (a.children) {
                  for (let i = 0; i < a.children.length; i++) {
                     const element = a.children[i];
                     let ReturnB = TestInHierarchy(element, id, _idx + 1)
                     if (ReturnB) {
                        return true
                     }
                  }
               }
               return false
            }

            let newLayerhi: LayerHierarchy = { ...state.layerHierarchy }
            if (!TestInHierarchy(state.layerHierarchy, newLayerInfo.layerId)) {
               newLayerhi.children = ([{ id: newLayerInfo.layerId }, ...newLayerhi.children]);
            }

            if (action.callbacks?.onLayerAdded) {
               action.callbacks.onLayerAdded(newLayerInfo);
            }

            return {
               ...state,
               layers: { ...state.layers, [_layerInfo.layerId]: { ...newLayerInfo } },
               layerHierarchy: newLayerhi as LayerHierarchy
            }
         } else {
            return state;
         }
      }

      case MapitStateActionType.LockUnlockDataLayer: {
         const layerInfo = { ...state.layers[action.payload.id] }
         if (action.payload.unlock) {
            layerInfo.unlocked = true
         } else {
            delete layerInfo.unlocked
         }
         return {
            ...state,
            layers: { ...state.layers, [action.payload.id]: layerInfo }
         }
      }

      case MapitStateActionType.MapitSetDrawContext: {
         return { ...state, drawContent: action.payload.drawContent }
      }

      case MapitStateActionType.RemoveDataLayer: {
         let handle = state.layers[action.payload.id].handle;
         handle.clearLayers(state.map)
         // clone to ensure that a changed object is returned to trigger effects
         let newLayers = { ...state.layers };
         delete newLayers[action.payload.id];
         let newHierarchy;
         if (!action.payload.keepInHierarchy) {
            newHierarchy = OldHierarchy.removeLayerInHierarchy(action.payload.id, state.layerHierarchy);
         }
         return {
            ...state,
            layers: newLayers,
            layerHierarchy: newHierarchy || state.layerHierarchy
         };
      }

      case MapitStateActionType.UpdateDataLayer: {
         const handle = (action.payload.layerInfo.handle as MitLayerHandleMaplibre)
         let { layerHandle } = MapitLayerFactory.createADataLayer(state.map, action.payload.layerInfo, state.mapInfo, {});
         if (layerHandle?.source) {
            handle.updateSource(
               state.map as MapFacadeMaplibre,
               layerHandle.source.type == "image" ? layerHandle.source : (layerHandle.source as M.GeoJSONSourceSpecification).data
            )
         }
         return {
            ...state, layers: { ...state.layers, [action.payload.id]: action.payload.layerInfo }
         }
      }

      case MapitStateActionType.ChangeMapStyle: {
         let nextStyle = action.payload.style

         if (nextStyle.name == "Blank") {
            nextStyle = {
               ...nextStyle,
               defaultBackgroundColor: action.payload.style.backgroundColor,
            }
         } else if (nextStyle.name && nextStyle.style) {
            nextStyle = {
               ...nextStyle,
               defaultStyle: { name: nextStyle.name, style: nextStyle.style },
            }
         }
         if (action.payload.userAction && !MitStorage.setValue('Mit-mapStyle', { ...state.mapInfo.mapStyle, ...nextStyle })) {
            console.warn("Failed to set map style in storage")
         }
         state.map.switchMapStyle(action.payload.style?.style ?? state.mapInfo.mapStyle.style, action.payload.style?.mods ?? state.mapInfo.mapStyle.mods)

         return {
            ...state, mapInfo: { ...state.mapInfo, mapStyle: { ...state.mapInfo.mapStyle, ...nextStyle } }
         }
      }

      // ----------------------------------------- catchments ----------------------------------------------------------------------

      // case MapitStateActionType.SetCatchmentState: {
      //    return {
      //       ...state,
      //       catchmentState: action.payload.state
      //    }

      // }
      // ----------------------------------------- demography report ---------------------------------------------------------------
      case MapitStateActionType.GenerateDemographyReportSuccess: {
         return {
            ...state,
            demographyReport: { metadata: action.payload.metadata, result: action.payload.result }
         }
      }

      default:
         throw new Error("Unknown action " + (action as MapitStateActions).type);
   }
}

// ---------------------------------------------------- generic stuff --------------------------------------------------------------

export const MapitStateContext = createContext<{
   state: MapitState,
   dispatch: React.Dispatch<MapitStateActions>,
   /** 
    * Returns the stacking order of the window or 0 if hidden
    * @example <caption>Hide inactive Window</caption>
    * if (!returnedValue) { return null }
    * @example <caption>Usage in zIndex</caption>
    * style={{zIndex:1000 + returnedValue , isolation:"isolate"}}
    * @returns 0 if Hidden, else return the stacking order of window
    */
}>({
   state: initialMapitState(),
   dispatch: () => undefined,
});

MapitStateContext.displayName = "MapitStateContext";


export class GenericTransactionManager {

   static dispatchMiddleware<ActionTypes>(dispatch: any, transactionalReducer: (action: any, dispatch: any) => any) {

      return (action: ActionTypes) => {
         transactionalReducer(action, dispatch);
      };

   }
}
